import React from "react";

import { useT } from "../i18n";
import { useTemplateVars } from "../i18n/vars";
import { SERVICES } from "../content/services";
import Seo from "../components/Seo";
import Reveal from "../components/motion/Reveal";
import { Section } from "../components/ui/primitives";
import PageHero from "../components/blocks/PageHero";
import HowPanel from "../components/blocks/HowPanel";
import PanelCta from "../components/blocks/PanelCta";
import OtherServices from "../components/blocks/OtherServices";
import { IndexedList, SlashList } from "../components/blocks/lists";

// One component renders all seven service pages. The content shape is
// identical for every service, so a design change lands on all of them at once
// and a new service is a dictionary entry plus one line in the registry.
//
// Block order is fixed and deliberate:
//   01 problem  -> establishes we know their situation
//   02 what     -> what can be ordered
//   03 how      -> the method. Raised panel, because it carries the page
//   04 apart    -> the differentiators

export default function ServicePage({ slug }) {
  const { t, tList } = useT();
  const vars = useTemplateVars();
  const base = `services.${slug}`;

  const position = SERVICES.findIndex((s) => s.slug === slug) + 1;
  const pad = (n) => String(n).padStart(2, "0");

  const deliverableKey = `${base}.deliverable`;
  const deliverable = t(deliverableKey);
  const hasDeliverable = deliverable !== deliverableKey;

  return (
    <>
      <Seo title={t(`${base}.title`)} description={t(`${base}.seoDescription`, vars)} />

      <PageHero
        eyebrow={t("services.heroEyebrow", { index: pad(position), total: pad(SERVICES.length) })}
        title={t(`${base}.title`)}
        lede={t(`${base}.tagline`)}
      />

      <Section index="01" title={t("services.blocks.problem")}>
        <Reveal>
          <p className="t-prose max-w-[700px]">{t(`${base}.problem`)}</p>
        </Reveal>
      </Section>

      <Section index="02" title={t("services.blocks.whatWeDo")}>
        <IndexedList items={tList(`${base}.whatWeDo`)} className="max-w-[700px]" />
      </Section>

      <HowPanel
        index="03"
        title={t("services.blocks.howWeDo")}
        note={t("services.blocks.howWeDoNote")}
        steps={tList(`${base}.howWeDo`)}
        deliverable={hasDeliverable ? deliverable : null}
        deliverableLabel={t("services.blocks.deliverable")}
      />

      <Section index="04" title={t("services.blocks.apart")}>
        <div className="max-w-[760px]">
          <SlashList items={tList(`${base}.apart`)} />
        </div>
      </Section>

      <PanelCta
        title={t("services.ctaTitle")}
        body={t("services.ctaBody")}
        cta={{ to: "/contact", label: t("services.ctaButton") }}
      />

      <OtherServices currentSlug={slug} />
    </>
  );
}
